import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Paper,
  Divider,
  MenuItem,
  Select,
  Tabs,
  Tab,
  useTheme
} from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import TimerIcon from '@mui/icons-material/Timer';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

export const CompanyOverview = () => {
  const theme = useTheme();
  const [filter, setFilter] = useState('quarterly'); // Filter for quarterly/yearly view
  const [selectedTab, setSelectedTab] = useState(0);
  const [animate, setAnimate] = useState(false);

  // Animation effect on component mount
  useEffect(() => {
    setAnimate(true);
  }, []);

  const handleFilterChange = (event) => {
    setFilter(event.target.value);
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  // Tab panel component
  const TabPanel = ({ children, value, index }) => {
    return (
      <div role="tabpanel" hidden={value !== index} style={{paddingTop: '20px'}}>
        {value === index && children}
      </div>
    );
  };

  // Group-level financial data
  const revenueData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Revenue (millions $)',
        data: [3.2, 3.5, 3.8, 4.2, 4.5, 4.7, 5.0, 5.2, 5.5, 5.8, 6.0, 6.3],
        borderColor: '#4caf50',
        backgroundColor: 'rgba(76, 175, 80, 0.2)',
        tension: 0.4,
      },
      {
        label: 'Expenses (millions $)',
        data: [2.8, 2.9, 3.1, 3.3, 3.4, 3.6, 3.8, 3.9, 4.1, 4.3, 4.5, 4.7],
        borderColor: '#f44336',
        backgroundColor: 'rgba(244, 67, 54, 0.2)',
        tension: 0.4,
      }
    ],
  };

  const profitData = {
    labels: ['Q1', 'Q2', 'Q3', 'Q4'],
    datasets: [
      {
        label: 'Profit (millions $)',
        data: [0.8, 1.2, 1.5, 1.8],
        backgroundColor: '#4caf50',
      },
      {
        label: 'Loss (millions $)',
        data: [0.2, 0.1, 0.2, 0.1],
        backgroundColor: '#f44336',
      },
    ],
  };

  // Subsidiary performance data
  const subsidiaryPerformanceData = {
    labels: ['ERP Systems', 'Oruland', 'IT Services'],
    datasets: [
      {
        label: 'Revenue (millions $)',
        data: [3.5, 1.8, 1.0],
        backgroundColor: ['#3f51b5', '#009688', '#ff9800'],
      },
    ],
  };

  const subsidiaryGrowthData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'ERP Systems',
        data: [1.2, 1.3, 1.4, 1.5, 1.6, 1.7, 1.8, 1.9, 2.0, 2.1, 2.2, 2.3],
        borderColor: '#3f51b5',
        backgroundColor: 'rgba(63, 81, 181, 0.2)',
        tension: 0.4,
      },
      {
        label: 'Oruland',
        data: [0.6, 0.7, 0.7, 0.8, 0.8, 0.9, 0.9, 1.0, 1.0, 1.1, 1.1, 1.2],
        borderColor: '#009688',
        backgroundColor: 'rgba(0, 150, 136, 0.2)',
        tension: 0.4,
      },
      {
        label: 'IT Services',
        data: [0.3, 0.3, 0.4, 0.4, 0.4, 0.5, 0.5, 0.5, 0.6, 0.6, 0.7, 0.7],
        borderColor: '#ff9800',
        backgroundColor: 'rgba(255, 152, 0, 0.2)',
        tension: 0.4,
      },
    ],
  };

  // Business metrics data
  const customerAcquisitionData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'New Customers',
        data: [45, 52, 60, 65, 72, 80, 85, 92, 98, 105, 112, 120],
        borderColor: '#2196f3',
        backgroundColor: 'rgba(33, 150, 243, 0.2)',
        tension: 0.4,
      },
      {
        label: 'Customer Churn',
        data: [5, 7, 6, 8, 7, 9, 8, 10, 9, 11, 10, 12],
        borderColor: '#f44336',
        backgroundColor: 'rgba(244, 67, 54, 0.2)',
        tension: 0.4,
      },
    ],
  };

  const employeeGrowthData = {
    labels: ['Q1', 'Q2', 'Q3', 'Q4'],
    datasets: [
      {
        label: 'Headcount',
        data: [85, 92, 100, 115],
        backgroundColor: '#9c27b0',
      },
    ],
  };
  
  return (
    <Box sx={{ 
      p: { xs: 2, md: 4 }, 
      maxWidth: '1400px', 
      margin: '0 auto',
      opacity: animate ? 1 : 0,
      transform: animate ? 'translateY(0)' : 'translateY(20px)',
      transition: 'opacity 0.8s ease, transform 0.8s ease',
    }}>
      <Typography 
        variant="h3" 
        gutterBottom 
        align="center" 
        sx={{ 
          mb: 5, 
          fontWeight: 700,
          background: 'linear-gradient(45deg, #1a237e 30%, #00695c 90%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          textShadow: '1px 1px 2px rgba(0,0,0,0.1)',
        }}
      >
        Company Overview
      </Typography>

      {/* Filter Section */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", marginBottom: "16px" }}>
        <Select
          value={filter}
          onChange={handleFilterChange}
          sx={{ minWidth: "150px" }}
        >
          <MenuItem value="monthly">Monthly View</MenuItem>
          <MenuItem value="quarterly">Quarterly View</MenuItem>
          <MenuItem value="yearly">Yearly View</MenuItem>
        </Select>
      </Box>

      {/* Section 1: Group-level snapshot */}
      <Typography 
        variant="h4" 
        sx={{ 
          mb: 3, 
          fontWeight: 600,
          position: 'relative',
          '&:after': {
            content: '""',
            position: 'absolute',
            bottom: '-8px',
            left: 0,
            width: '60px',
            height: '4px',
            backgroundColor: '#1a237e',
            borderRadius: '2px'
          }
        }}
      >
        Group-Level Financial Snapshot
      </Typography>

      <Grid container spacing={3} sx={{ marginBottom: "32px" }}>
        {/* Summary Cards */}
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={3} sx={{ 
            height: '100%',
            transition: 'transform 0.3s ease-in-out',
            '&:hover': {
              transform: 'translateY(-5px)',
              boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>Total Revenue</Typography>
                <MonetizationOnIcon sx={{ color: '#4caf50', fontSize: 30 }} />
              </Box>
              <Typography variant="h4" sx={{ color: "#4caf50", fontWeight: "bold" }}>
                $6.3M
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <TrendingUpIcon sx={{ color: '#4caf50', mr: 0.5, fontSize: 16 }} />
                <Typography variant="body2" sx={{ color: '#4caf50' }}>
                  +12.5% vs last year
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={3} sx={{ 
            height: '100%',
            transition: 'transform 0.3s ease-in-out',
            '&:hover': {
              transform: 'translateY(-5px)',
              boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>Burn Rate</Typography>
                <LocalFireDepartmentIcon sx={{ color: '#f44336', fontSize: 30 }} />
              </Box>
              <Typography variant="h4" sx={{ color: "#f44336", fontWeight: "bold" }}>
                $390K
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <TrendingDownIcon sx={{ color: '#4caf50', mr: 0.5, fontSize: 16 }} />
                <Typography variant="body2" sx={{ color: '#4caf50' }}>
                  -5.2% vs last month
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={3} sx={{ 
            height: '100%',
            transition: 'transform 0.3s ease-in-out',
            '&:hover': {
              transform: 'translateY(-5px)',
              boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>Runway</Typography>
                <TimerIcon sx={{ color: '#2196f3', fontSize: 30 }} />
              </Box>
              <Typography variant="h4" sx={{ color: "#2196f3", fontWeight: "bold" }}>
                18 months
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <TrendingUpIcon sx={{ color: '#4caf50', mr: 0.5, fontSize: 16 }} />
                <Typography variant="body2" sx={{ color: '#4caf50' }}>
                  +2 months vs Q1
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={3} sx={{ 
            height: '100%',
            transition: 'transform 0.3s ease-in-out',
            '&:hover': {
              transform: 'translateY(-5px)',
              boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>Net Profit</Typography>
                <AccountBalanceWalletIcon sx={{ color: '#9c27b0', fontSize: 30 }} />
              </Box>
              <Typography variant="h4" sx={{ color: "#9c27b0", fontWeight: "bold" }}>
                $1.8M
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <TrendingUpIcon sx={{ color: '#4caf50', mr: 0.5, fontSize: 16 }} />
                <Typography variant="body2" sx={{ color: '#4caf50' }}>
                  +20% vs last quarter
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Revenue vs Expenses Chart */}
        <Grid item xs={12} md={8}>
          <Paper elevation={3} sx={{ padding: "20px" }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", marginBottom: "16px" }}>
              Revenue vs Expenses
            </Typography>
            <Line data={revenueData} options={{ 
              responsive: true,
              plugins: {
                legend: {
                  position: 'top',
                },
                tooltip: {
                  mode: 'index',
                  intersect: false,
                }
              },
              scales: {
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: 'Millions ($)'
                  }
                }
              }
            }} />
          </Paper>
        </Grid>
        
        {/* Profit/Loss Chart */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ padding: "20px" }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", marginBottom: "16px" }}>
              Quarterly Profit/Loss
            </Typography>
            <Bar data={profitData} options={{ 
              responsive: true,
              plugins: {
                legend: {
                  position: 'top',
                },
                tooltip: {
                  mode: 'index',
                  intersect: false,
                }
              },
              scales: {
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: 'Millions ($)'
                  }
                }
              }
            }} />
          </Paper>
        </Grid>
      </Grid>

      {/* Section 2: Subsidiary/Vertical Performance */}
      <Typography 
        variant="h4" 
        sx={{ 
          mb: 3, 
          mt: 5,
          fontWeight: 600,
          position: 'relative',
          '&:after': {
            content: '""',
            position: 'absolute',
            bottom: '-8px',
            left: 0,
            width: '60px',
            height: '4px',
            backgroundColor: '#00695c',
            borderRadius: '2px'
          }
        }}
      >
        Subsidiary/Vertical Performance
      </Typography>

      <Grid container spacing={3} sx={{ marginBottom: "32px" }}>
        {/* Subsidiary Revenue Distribution */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ padding: "20px", height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", marginBottom: "16px" }}>
              Revenue Distribution
            </Typography>
            <Doughnut data={subsidiaryPerformanceData} options={{ 
              responsive: true,
              plugins: {
                legend: {
                  position: 'bottom',
                },
                tooltip: {
                  callbacks: {
                    label: function(context) {
                      const label = context.label || '';
                      const value = context.raw || 0;
                      const total = context.dataset.data.reduce((a, b) => a + b, 0);
                      const percentage = Math.round((value / total) * 100);
                      return `${label}: $${value}M (${percentage}%)`;
                    }
                  }
                }
              },
              cutout: '65%',
            }} />
          </Paper>
        </Grid>
        
        {/* Subsidiary Growth Chart */}
        <Grid item xs={12} md={8}>
          <Paper elevation={3} sx={{ padding: "20px" }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", marginBottom: "16px" }}>
              Vertical Growth Trends
            </Typography>
            <Line data={subsidiaryGrowthData} options={{ 
              responsive: true,
              plugins: {
                legend: {
                  position: 'top',
                },
                tooltip: {
                  mode: 'index',
                  intersect: false,
                }
              },
              scales: {
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: 'Revenue (Millions $)'
                  }
                }
              }
            }} />
          </Paper>
        </Grid>

        {/* Subsidiary Performance Summary */}
        <Grid item xs={12}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: "bold", marginBottom: "16px" }}>
                Vertical Performance Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Paper elevation={2} sx={{ p: 2, backgroundColor: 'rgba(63, 81, 181, 0.05)', borderLeft: '4px solid #3f51b5' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: '#3f51b5' }}>
                      ERP Systems
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Revenue:</strong> $3.5M (55% of total)
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Growth:</strong> +18% YoY
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Customers:</strong> 85 enterprise clients
                    </Typography>
                    <Typography variant="body2">
                      <strong>Team Size:</strong> 42 employees
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Paper elevation={2} sx={{ p: 2, backgroundColor: 'rgba(0, 150, 136, 0.05)', borderLeft: '4px solid #009688' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: '#009688' }}>
                      Oruland
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Revenue:</strong> $1.8M (28% of total)
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Growth:</strong> +25% YoY
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Customers:</strong> 3,200 users
                    </Typography>
                    <Typography variant="body2">
                      <strong>Team Size:</strong> 28 employees
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Paper elevation={2} sx={{ p: 2, backgroundColor: 'rgba(255, 152, 0, 0.05)', borderLeft: '4px solid #ff9800' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: '#ff9800' }}>
                      IT Services
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Revenue:</strong> $1.0M (17% of total)
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Growth:</strong> +12% YoY
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Clients:</strong> 35 businesses
                    </Typography>
                    <Typography variant="body2">
                      <strong>Team Size:</strong> 15 employees
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Section 3: Key Business Metrics and Trends */}
      <Typography 
        variant="h4" 
        sx={{ 
          mb: 3, 
          mt: 5,
          fontWeight: 600,
          position: 'relative',
          '&:after': {
            content: '""',
            position: 'absolute',
            bottom: '-8px',
            left: 0,
            width: '60px',
            height: '4px',
            backgroundColor: '#9c27b0',
            borderRadius: '2px'
          }
        }}
      >
        Key Business Metrics & Trends
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={selectedTab} onChange={handleTabChange} aria-label="metrics tabs">
          <Tab label="Customers" />
          <Tab label="Employees" />
          <Tab label="Operations" />
        </Tabs>
      </Box>
      
      {/* Customer Metrics Tab */}
      <TabPanel value={selectedTab} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper elevation={3} sx={{ padding: "20px" }}>
              <Typography variant="h6" sx={{ fontWeight: "bold", marginBottom: "16px" }}>
                Customer Acquisition & Churn
              </Typography>
              <Line data={customerAcquisitionData} options={{ 
                responsive: true,
                plugins: {
                  legend: {
                    position: 'top',
                  },
                  tooltip: {
                    mode: 'index',
                    intersect: false,
                  }
                }
              }} />
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card elevation={3}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: "bold", marginBottom: "16px" }}>
                  Customer KPIs
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Customer Acquisition Cost</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontWeight: "bold", color: "#2196f3" }}>$850</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <TrendingDownIcon sx={{ color: '#4caf50', fontSize: 16 }} />
                      <Typography variant="body2" sx={{ color: '#4caf50' }}>
                        -5%
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Customer Lifetime Value</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontWeight: "bold", color: "#4caf50" }}>$9,200</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <TrendingUpIcon sx={{ color: '#4caf50', fontSize: 16 }} />
                      <Typography variant="body2" sx={{ color: '#4caf50' }}>
                        +8%
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Churn Rate</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontWeight: "bold", color: "#ff9800" }}>4.2%</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <TrendingDownIcon sx={{ color: '#4caf50', fontSize: 16 }} />
                      <Typography variant="body2" sx={{ color: '#4caf50' }}>
                        -0.8%
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Box>
                  <Typography variant="subtitle2">Net Promoter Score</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontWeight: "bold", color: "#9c27b0" }}>65</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <TrendingUpIcon sx={{ color: '#4caf50', fontSize: 16 }} />
                      <Typography variant="body2" sx={{ color: '#4caf50' }}>
                        +5 points
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>
      
      {/* Employee Metrics Tab */}
      <TabPanel value={selectedTab} index={1}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ padding: "20px" }}>
              <Typography variant="h6" sx={{ fontWeight: "bold", marginBottom: "16px" }}>
                Employee Headcount Growth
              </Typography>
              <Bar data={employeeGrowthData} options={{ 
                responsive: true,
                plugins: {
                  legend: {
                    display: false,
                  }
                }
              }} />
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card elevation={3}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: "bold", marginBottom: "16px" }}>
                  Employee Metrics
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Paper elevation={1} sx={{ p: 2 }}>
                      <Typography variant="subtitle2">Retention Rate</Typography>
                      <Typography variant="h5" sx={{ fontWeight: "bold", color: "#4caf50" }}>92%</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TrendingUpIcon sx={{ color: '#4caf50', fontSize: 14, mr: 0.5 }} />
                        <Typography variant="body2" sx={{ color: '#4caf50' }}>
                          +3% vs last year
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Paper elevation={1} sx={{ p: 2 }}>
                      <Typography variant="subtitle2">Productivity</Typography>
                      <Typography variant="h5" sx={{ fontWeight: "bold", color: "#2196f3" }}>87%</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TrendingUpIcon sx={{ color: '#4caf50', fontSize: 14, mr: 0.5 }} />
                        <Typography variant="body2" sx={{ color: '#4caf50' }}>
                          +5% vs last quarter
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Paper elevation={1} sx={{ p: 2 }}>
                      <Typography variant="subtitle2">Time to Hire</Typography>
                      <Typography variant="h5" sx={{ fontWeight: "bold", color: "#ff9800" }}>23 days</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TrendingDownIcon sx={{ color: '#4caf50', fontSize: 14, mr: 0.5 }} />
                        <Typography variant="body2" sx={{ color: '#4caf50' }}>
                          -5 days vs last year
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Paper elevation={1} sx={{ p: 2 }}>
                      <Typography variant="subtitle2">Training Compliance</Typography>
                      <Typography variant="h5" sx={{ fontWeight: "bold", color: "#9c27b0" }}>96%</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TrendingUpIcon sx={{ color: '#4caf50', fontSize: 14, mr: 0.5 }} />
                        <Typography variant="body2" sx={{ color: '#4caf50' }}>
                          +2% vs target
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>
      
      {/* Operations Metrics Tab */}
      <TabPanel value={selectedTab} index={2}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card elevation={3}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: "bold", marginBottom: "16px" }}>
                  Operational KPIs
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Paper elevation={1} sx={{ p: 2, mb: 2, borderLeft: '4px solid #3f51b5' }}>
                      <Typography variant="subtitle2">Project Delivery On Time</Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: "bold", color: "#3f51b5" }}>92%</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <TrendingUpIcon sx={{ color: '#4caf50', fontSize: 16 }} />
                          <Typography variant="body2" sx={{ color: '#4caf50' }}>
                            +4%
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Paper elevation={1} sx={{ p: 2, mb: 2, borderLeft: '4px solid #009688' }}>
                      <Typography variant="subtitle2">System Uptime</Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: "bold", color: "#009688" }}>99.97%</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <TrendingUpIcon sx={{ color: '#4caf50', fontSize: 16 }} />
                          <Typography variant="body2" sx={{ color: '#4caf50' }}>
                            +0.05%
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Paper elevation={1} sx={{ p: 2, mb: 2, borderLeft: '4px solid #f44336' }}>
                      <Typography variant="subtitle2">Support Ticket Resolution Time</Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: "bold", color: "#f44336" }}>4.2 hours</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <TrendingDownIcon sx={{ color: '#4caf50', fontSize: 16 }} />
                          <Typography variant="body2" sx={{ color: '#4caf50' }}>
                            -0.8 hours
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Paper elevation={1} sx={{ p: 2, borderLeft: '4px solid #ff9800' }}>
                      <Typography variant="subtitle2">User Adoption Rate</Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: "bold", color: "#ff9800" }}>78%</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <TrendingUpIcon sx={{ color: '#4caf50', fontSize: 16 }} />
                          <Typography variant="body2" sx={{ color: '#4caf50' }}>
                            +6%
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card elevation={3}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: "bold", marginBottom: "16px" }}>
                  Key Insights & Recommendations
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: '600', color: '#3f51b5' }}>
                    Growth Opportunities
                  </Typography>
                  <Typography variant="body2" paragraph>
                    • ERP Systems showing strongest growth; consider additional investment in this area
                  </Typography>
                  <Typography variant="body2" paragraph>
                    • Oruland has highest growth rate; scaling team recommended
                  </Typography>
                  <Typography variant="body2" paragraph>
                    • Cross-selling opportunities between ERP and IT Services clients
                  </Typography>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: '600', color: '#f44336' }}>
                    Risk Areas
                  </Typography>
                  <Typography variant="body2" paragraph>
                    • IT Services growth rate below target; review strategy
                  </Typography>
                  <Typography variant="body2" paragraph>
                    • Customer acquisition costs still above industry benchmark
                  </Typography>
                  <Typography variant="body2" paragraph>
                    • Technical staff recruitment challenges affecting scaling capacity
                  </Typography>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: '600', color: '#4caf50' }}>
                    Next Steps
                  </Typography>
                  <Typography variant="body2" paragraph>
                    • Board review of Q4 growth targets scheduled for May 5th
                  </Typography>
                  <Typography variant="body2" paragraph>
                    • Strategic planning session for international expansion in June
                  </Typography>
                  <Typography variant="body2">
                    • New product roadmap presentation from CPO due next week
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>
    </Box>
  );
};

export default CompanyOverview;